'use client';
import React from 'react';
import { Section, Container } from '@layout';
import { Button, Card, Heading } from '@ui';
import MarkupSnippet, { markupCopyLabels } from '../markup-snippet/MarkupSnippet';

interface CardsProps {
  sectionID?: string;
  classes?: string;
  containerId?: string;
  containerClasses?: string;
  isSpanish?: boolean;
}

const Cards: React.FC<CardsProps> = ({
  isSpanish = false,
  sectionID = '',
  classes = '',
  containerId,
  containerClasses,
}) => {
  const t = (en: string, es: string) => (isSpanish ? es : en);

  return (
    <Section sectionID={sectionID} className={classes}>
      <Container containerId={containerId} containerClasses={containerClasses}>
        <Heading as='h3'>{t('Cards', 'Tarjetas')}</Heading>
        <p className='sub full'>
          {t(
            'Modular card layouts with header support, variant styling, and interactive hover/focus states for clear content grouping and visual hierarchy.',
            'Diseños de tarjetas modulares con soporte de encabezado, estilos de variantes y estados interactivos de hover/focus para agrupar el contenido de forma clara y mantener una jerarquía visual.'
          )}
        </p>

        <Card className='surface-1 cards-demo__composition'>
          <Heading as='h4' style={{ marginTop: 0 }}>
            {t('Quarterly roadmap', 'Hoja de ruta trimestral')}
          </Heading>
          <p className='sub full' style={{ marginBottom: '0.8rem' }}>
            {t(
              'Prioritized themes for the next cycle, with owners and target dates.',
              'Temas priorizados para el siguiente ciclo, con responsables y fechas objetivo.'
            )}
          </p>
          <p className='small' style={{ color: 'var(--muted)', marginBottom: '1.2rem' }}>
            {t(
              'Updated 2h ago · Strategy · 4 owners',
              'Actualizado hace 2 h · Estrategia · 4 responsables'
            )}
          </p>
          <div className='row btn-row gap'>
            <Button type='button' label={t('View plan', 'Ver plan')} className='btn-primary' />
            <Button type='button' label={t('Share', 'Compartir')} className='btn-secondary' />
          </div>
        </Card>

        <Card className='grid-3'>
          <Card header={t('With Header', 'Con Encabezado')} headerType='h4' headerClasses='h3'>
            <p>
              {t(
                'This card has a header with an icon and text.',
                'Esta tarjeta tiene un encabezado con un icono y texto.'
              )}
            </p>
          </Card>
          <Card>
            <p>
              {t(
                'This card has no header. Just content.',
                'Esta tarjeta no tiene encabezado. Solo contenido.'
              )}
            </p>
          </Card>
          <Card className='card-0'>
            <p>{t('Card 0 alternate styling.', 'Tarjeta 0: estilo alternativo.')}</p>
          </Card>
          <Card className='card-1'>
            <p>{t('Card 1 alternate styling.', 'Tarjeta 1: estilo alternativo.')}</p>
          </Card>
          <Card className='card-2'>
            <p>{t('Card 2 alternate styling.', 'Tarjeta 2: estilo alternativo.')}</p>
          </Card>
          <Card className='card-inverse'>
            <p>{t('Card inverse styling.', 'Tarjeta con estilo inverso.')}</p>
          </Card>
        </Card>
        <MarkupSnippet
          {...markupCopyLabels(isSpanish)}
          code={`<article class="card">
  <div class="card-header"><h3>Title</h3></div>
  <p>Body copy</p>
  <div class="row btn-row gap">
    <button class="btn btn-primary" type="button"><span class="label">View plan</span></button>
    <button class="btn btn-secondary" type="button"><span class="label">Share</span></button>
  </div>
</article>
<article class="card card-0">Card 0</article>
<article class="card card-1">Card 1</article>
<article class="card card-2">Card 2</article>
<article class="card card-inverse">Inverse</article>`}
        />
      </Container>
    </Section>
  );
};

export default Cards;
